import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Play, Timer, ArrowRight } from "lucide-react";
import { publicApi, videoUrl } from "@/lib/api";
import { ShareBar } from "@/components/ShareBar";
import { useStreak, StreakNote } from "@/components/DevotionalStreak";
import { useDevotionalsOn } from "@/lib/display";

const poster = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/devotionals/${id}/poster.jpg`;

const Phone = ({ dev, onPlay }) => {
  const [playing, setPlaying] = useState(false);
  const start = () => { setPlaying(true); onPlay(); };
  return (
    <div className="relative mx-auto w-[260px] shrink-0 rounded-[2.6rem] bg-slate-900 p-2.5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/10 sm:w-[300px]" data-testid="devotional-today-phone">
      <div className="absolute left-1/2 top-4 z-10 h-5 w-20 -translate-x-1/2 rounded-full bg-black" />
      <div className="relative aspect-[9/19] overflow-hidden rounded-[2.1rem] bg-black">
        {playing ? (
          <video src={videoUrl(dev.id)} poster={poster(dev.id)} controls autoPlay playsInline className="h-full w-full object-cover" data-testid="devotional-today-video" />
        ) : (
          <button onClick={start} className="group absolute inset-0" data-testid="devotional-today-play">
            <img src={poster(dev.id)} alt="" className="h-full w-full object-cover opacity-85" />
            <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            <span className="absolute left-1/2 top-1/2 grid h-20 w-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[#0A3A6B] shadow-xl transition-transform duration-300 group-hover:scale-110">
              <span className="absolute inset-0 animate-ping rounded-full bg-white/40" />
              <Play className="relative ml-1 h-8 w-8 fill-current" />
            </span>
            <span className="absolute bottom-5 left-5 right-5 text-left text-sm font-semibold text-white">{dev.duration ? `${Math.floor(dev.duration / 60)}:${String(Math.round(dev.duration % 60)).padStart(2, "0")}` : "2 min"} · Tap to watch</span>
          </button>
        )}
      </div>
    </div>
  );
};

const Recent = ({ items }) => (
  <div className="mt-10" data-testid="devotional-recent">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200/80">Catch up</p>
    <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
      {items.map((d) => (
        <Link key={d.id} to={`/devotionals?d=${d.id}`} className="group w-28 shrink-0" data-testid={`devotional-recent-${d.id}`}>
          <div className="relative aspect-[9/14] overflow-hidden rounded-2xl bg-black ring-1 ring-white/10">
            <img src={poster(d.id)} alt="" loading="lazy" className="h-full w-full object-cover opacity-85 transition-transform duration-500 group-hover:scale-105" />
            <Play className="absolute bottom-2 right-2 h-4 w-4 fill-white text-white" />
          </div>
          <p className="mt-1.5 line-clamp-2 text-xs font-medium text-white/90">{d.title}</p>
        </Link>
      ))}
    </div>
  </div>
);

export const DevotionalToday = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [played, setPlayed] = useState(false);
  const { streak, isMember, onWatch } = useStreak();
  const on = useDevotionalsOn();
  useEffect(() => { publicApi.get("/devotionals").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true)); }, []);
  const dev = items[0];
  const isToday = dev?.devotional_date === format(new Date(), "yyyy-MM-dd");
  const onPlay = () => { setPlayed(true); if (isToday) onWatch(); };
  if (!loaded || !on) return null;
  return (
    <section id="devotional" className="relative overflow-hidden bg-[#0A3A6B] py-20 text-white sm:py-28" data-testid="devotional-today-section">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#1D64B4]/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -left-32 h-[420px] w-[420px] rounded-full bg-sky-400/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-[auto_1fr] lg:gap-24 lg:px-10">
        {dev ? <Phone dev={dev} onPlay={onPlay} /> : (
          <div className="mx-auto grid aspect-[4/3] w-[260px] place-items-center rounded-[2rem] border border-dashed border-white/30 p-8 text-center text-sm text-sky-100 lg:aspect-[9/19]" data-testid="devotional-today-empty">Our first 2-minute devotional is on its way.</div>
        )}
        <div className="max-w-xl">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200"><Timer className="h-4 w-4" /> {dev && isToday ? "Today's 2-minute devotional" : "Daily 2-minute devotional"}</p>
          <h2 className="mt-4 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl" data-testid="devotional-today-title">{dev ? dev.title : "Two minutes with God, every day."}</h2>
          {dev && <p className="mt-3 text-sky-100/90">{dev.speaker} · {format(parseISO(dev.devotional_date), "EEEE, MMMM d")}</p>}
          {dev?.scripture && <p className="font-serif-accent mt-6 border-l-2 border-amber-300 pl-4 text-2xl text-white/95">{dev.scripture}</p>}
          <p className="mt-6 text-base text-sky-100/80">Short, daily encouragement from our pastors, staff and leaders. Watch it over coffee, on the jeep, or before you start your day.</p>
          {played && isToday && isMember && <div className="mt-6 max-w-md text-slate-900"><StreakNote streak={streak} /></div>}
          {dev && <div className="mt-8 [&_a]:border-white/30 [&_a]:bg-white/10 [&_a]:text-white [&_button]:border-white/30"><ShareBar title={dev.title} url={`${process.env.REACT_APP_BACKEND_URL}/api/share/devotionals/${dev.id}`} testPrefix="devotional-today-share" /></div>}
          <Link to="/devotionals" className="group mt-8 inline-flex items-center gap-2 font-semibold text-white" data-testid="devotional-today-all">See all devotionals <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          {items.length > 1 && <Recent items={items.slice(1, 7)} />}
        </div>
      </div>
    </section>
  );
};
